/**
 * 올린 HTML 이 바깥에서 불러오는 곳(2026-11-03).
 *
 * 화면은 올린 HTML 을 출처 없는 샌드박스에 띄우고, 바깥 길은 https 만 연다(`HTML_CSP`). 그래서 HTML 이 CDN 의 스크립트·글꼴·
 * 그림을 부르면 **그 부분은 인터넷이 있어야 나오고**(폐쇄망 현장에서는 조용히 빈다), 그 바깥 파일이 바뀌면 콘솔에서 승인한
 * 뒤에도 화면 내용이 바뀐다. 운영자가 그것을 알고 고르게, 서버가 받을 때 여기로 훑어 바깥 출처를 적어 둔다(자산의
 * `html_external`). 고객사가 바깥 연결을 막아 두었으면(`html_offline`) 화면은 그 부분을 아예 부르지 않는다.
 *
 * 훑는 곳 — 문서가 **불러오는** 자리만: 태그의 `src`·`srcset`·`poster`·`data`, `<link href>`, 스타일의 `url()`·`@import`, 그리고
 * 스크립트 안의 주소 글자(`fetch`·`WebSocket` 등으로 부를 수 있다). 누르면 가는 링크(`<a href>`)는 화면에서 누를 수 없으니
 * 빼고, SVG 이름공간(`www.w3.org`)처럼 주소 꼴이지만 부르지 않는 것도 뺀다. 글자만 보는 짐작이라 스크립트가 주소를 이어
 * 붙여 만들면 놓칠 수 있다 — 막기는 샌드박스·CSP 가 하고, 이것은 알리기만 한다.
 */
/** 적어 두는 출처 수의 상한 — 그보다 많으면 앞의 것만. */
export const HTML_EXTERNAL_MAX = 30;
/** 주소 꼴이지만 불러오지 않는 이름공간 — 스크립트의 `createElementNS('http://www.w3.org/2000/svg', …)` 같은. */
const NAMESPACE_HOSTS = new Set(['www.w3.org', 'w3.org']);
const ORIGIN = /^((?:https?|wss?):)?\/\/([a-z0-9.-]+(?::\d+)?)/i;
/** 주소에서 출처(`https://cdn.example.com`)를 — 바깥 주소가 아니면 null. `//cdn…` 은 https 로 본다. */
function originOf(url) {
    const match = ORIGIN.exec(url.trim());
    if (!match)
        return null;
    const host = (match[2] ?? '').toLowerCase().replace(/\.$/, '');
    if (!host || NAMESPACE_HOSTS.has(host.replace(/:\d+$/, '')))
        return null;
    const scheme = (match[1] ?? 'https:').toLowerCase();
    return `${scheme}//${host}`;
}
/** 문서가 바깥에서 불러오는 출처들 — 겹치지 않게, 이름 차례로, `HTML_EXTERNAL_MAX` 까지. */
export function htmlExternalOrigins(html) {
    const found = [];
    const add = (url) => {
        if (!url || found.length >= HTML_EXTERNAL_MAX)
            return;
        const origin = originOf(url);
        if (origin && !found.includes(origin))
            found.push(origin);
    };
    // 스크립트·스타일 안은 따로 훑고, 태그를 훑을 때는 그 내용을 지운다(글자 속 `<img src=…>` 를 태그로 읽지 않게).
    let markup = html.replace(/<!--[\s\S]*?-->/g, ' ');
    markup = markup.replace(/<script\b[^>]*>([\s\S]*?)<\/script\s*>/gi, (whole, body) => {
        for (const literal of body.matchAll(/["'`]((?:https?|wss?):\/\/[^"'`\s]+)/gi))
            add(literal[1]);
        return whole.slice(0, whole.indexOf('>') + 1);
    });
    const css = (text) => {
        for (const match of text.matchAll(/url\(\s*["']?([^"')\s]+)/gi))
            add(match[1]);
        for (const match of text.matchAll(/@import\s+(?:url\()?\s*["']?([^"')\s;]+)/gi))
            add(match[1]);
    };
    markup = markup.replace(/<style\b[^>]*>([\s\S]*?)<\/style\s*>/gi, (whole, body) => {
        css(body);
        return '';
    });
    for (const tag of markup.matchAll(/<([a-z][a-z0-9-]*)\b([^>]*)>/gi)) {
        const name = (tag[1] ?? '').toLowerCase();
        const attributes = tag[2] ?? '';
        for (const attribute of attributes.matchAll(/([a-z:-]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/gi)) {
            const key = (attribute[1] ?? '').toLowerCase();
            const value = attribute[2] ?? attribute[3] ?? attribute[4] ?? '';
            if (key === 'style')
                css(value);
            else if (key === 'srcset')
                for (const part of value.split(','))
                    add(part.trim().split(/\s+/)[0]);
            else if (key === 'src' || key === 'poster' || key === 'data' || key === 'background')
                add(value);
            else if (key === 'href' && name === 'link')
                add(value);
        }
    }
    return found.sort();
}
/** 화면에서 열리지 않는 출처 — 화면은 https·wss 만 연다(`HTML_CSP`). */
export function insecureOrigin(origin) {
    return /^(http|ws):/i.test(origin);
}
//# sourceMappingURL=html-external.js.map